import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AWARD_RULE_TEXT } from '../lib/awards';
import { AwardBadge } from '../components/AwardBadge';
import { Trophy, Clock, Users, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const ChallengesPage = () => {
  const { api, isModerator } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const res = await api.get('/challenges');
        setChallenges(res.data);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, [api]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'judging': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredChallenges = challenges.filter(c => {
    if (activeTab === 'all') return true;
    return c.status === activeTab;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="challenges-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.pexels.com/photos/4186911/pexels-photo-4186911.jpeg"
            alt="Challenges"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/90 to-brand-primary/70" />
        </div>
        <div className="relative p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="text-white">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-6 h-6 text-brand-tertiary" />
                <span className="overline text-brand-tertiary">Writing Challenges</span>
              </div>
              <h1 className="font-serif text-4xl font-semibold mb-4">Test Your Craft</h1>
              <p className="text-white/80 max-w-lg">
                Participate in bi-weekly challenges judged on quality, not popularity.{' '}
                <span data-testid="challenges-award-rule-text">{AWARD_RULE_TEXT}</span>
              </p>
            </div>
            {isModerator && (
              <Link to="/admin/challenges/new" data-testid="create-challenge-button">
                <Button className="bg-white text-brand-primary hover:bg-white/90 gap-2">
                  <Sparkles className="w-4 h-4" />
                  Create Challenge
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
        <TabsList className="bg-surface-alt">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="judging" data-testid="tab-judging">Judging</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Challenges Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : filteredChallenges.length === 0 ? (
        <div className="text-center py-20">
          <Trophy className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
            No challenges found
          </h2>
          <p className="text-text-secondary">
            {activeTab === 'all' 
              ? 'Check back soon for new writing challenges!'
              : `No ${activeTab} challenges at the moment.`}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {filteredChallenges.map((challenge) => (
            <Link 
              key={challenge.id} 
              to={`/challenges/${challenge.id}`}
              className="group"
              data-testid={`challenge-card-${challenge.id}`}
            >
              <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                {challenge.cover_image && (
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={challenge.cover_image}
                      alt={challenge.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Badge className={getStatusColor(challenge.status)}>
                      {challenge.status.charAt(0).toUpperCase() + challenge.status.slice(1)}
                    </Badge>
                    {challenge.genre_restriction && (
                      <span className="text-xs text-text-muted">{challenge.genre_restriction}</span>
                    )}
                  </div>

                  <h3 className="font-serif text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                    {challenge.title}
                  </h3>

                  <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                    {challenge.description}
                  </p>

                  {/* Awards — only shown once results are announced and assigned */}
                  {challenge.results_announced && challenge.awarded?.length > 0 && (
                    <div className="flex flex-col gap-1.5 mb-4" data-testid={`challenge-card-awards-${challenge.id}`}>
                      {challenge.awarded.map((a) => (
                        <div key={`${a.award}-${a.story_id}`} className="flex items-center gap-2 min-w-0">
                          <AwardBadge award={a.award} />
                          <span className="text-xs text-text-secondary truncate">{a.story_title}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Meta Info */}
                  <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10 text-sm text-text-muted">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {challenge.entry_count} {challenge.entry_count === 1 ? 'entry' : 'entries'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(challenge.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Judges — only show after results announced or on completed challenges */}
                  {challenge.judges && challenge.judges.length > 0 && challenge.results_announced && (
                    <div className="mt-4 pt-4 border-t border-brand-primary/10">
                      <p className="text-xs text-text-muted mb-1">Judges:</p>
                      <p className="text-sm text-text-secondary">
                        {challenge.judges.map(j => j.name).join(', ')}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChallengesPage;
